/**
 * @fileoverview Provide the single-day timed/all-day layout for caller-supplied calendar records. `kc-internal-frontend/src/components/Calendar/BespokeCalendar.js` owns navigation; shared calendar utilities and EventComponent own time/layout conventions and action signifiers. The view delegates event/day selection without owning event persistence or authorization.
 */

import React, { useMemo, useState } from 'react';
import moment from 'moment-timezone';
import EventComponent from '../components/EventComponent';
import AppIcon from '../../shared/AppIcon';
import { 
  generateHourSlots, 
  isToday, 
  calculateEventOverlaps,
  CALENDAR_VIEWS,
  formatTime,
  parseEventDate
} from '../utils/calendarUtils';

const MAX_COLLAPSED_ALL_DAY_EVENTS = 4;



const DayView = ({ 
  currentDate, 
  events = [],
  onEventClick,
  onEventEdit,
  onEventDelete,
  onConfirmResponsibility,
  onTimeSlotClick,
  onEventDrop,
  assignees = [],
  currentUser = null
}) => {
  const [isAllDayExpanded, setIsAllDayExpanded] = useState(false);
  // Generate hour slots
  const hourSlots = useMemo(() => generateHourSlots(), []);
  
  // Filter and process events for the current day
  const dayEvents = useMemo(() => {
    const dateKey = currentDate.format('YYYY-MM-DD');
    const filteredEvents = events.filter(event => {
      const eventStart = parseEventDate(event.start, event.allDay);
      const eventEnd = parseEventDate(event.end, event.allDay);
      return eventStart.format('YYYY-MM-DD') === dateKey || 
             eventEnd.format('YYYY-MM-DD') === dateKey ||
             (eventStart.isBefore(currentDate, 'day') && eventEnd.isAfter(currentDate, 'day'));
    });
    
    return filteredEvents;
  }, [events, currentDate]);

  // Separate all-day and timed events
  const { allDayEvents, timedEvents } = useMemo(

  () => {
    const allDay = dayEvents.filter(event => event.allDay);
    const timed = dayEvents.filter(event => !event.allDay);
    
    // Calculate overlaps for timed events
    const timedWithOverlaps = calculateEventOverlaps(timed);
    
    return {
      allDayEvents: allDay,
      timedEvents: timedWithOverlaps
    };
  }, [dayEvents]);

  // Calculate event style for day view positioning
  const calculateEventStyle =

  (event) => {
    const eventStart = moment(event.start);
    const eventEnd = moment(event.end);
    
    // Calculate position
    const startHour = eventStart.hour() + (eventStart.minute() / 60);
    const endHour = eventEnd.hour() + (eventEnd.minute() / 60);
    const duration = endHour - startHour;
    
    // Time slot height in pixels (each hour is 60px)
    const hourHeight = 60;
    const top = startHour * hourHeight;
    const height = Math.max(duration * hourHeight, 30); // Minimum 30px height
    
    // Handle overlapping events with better spacing
    const overlapAdjustment = event.totalOverlapping > 1 ? 
      (100 / event.totalOverlapping) : 100;
    const leftOffset = event.overlapIndex * overlapAdjustment;
    
    return {
      top: `${top}px`,
      height: `${height}px`,
      left: `calc(${leftOffset}% + 2px)`,
      width: `calc(${overlapAdjustment}% - 4px)`,
      zIndex: 10 + event.overlapIndex
    };
  };

  // Handle time slot click for creating new events
  const handleTimeSlotClick =

  (hour, e) => {
    e.stopPropagation();
    if (onTimeSlotClick) {
      const clickTime = moment(currentDate).hour(Math.floor(hour)).minute((hour % 1) * 60);
      onTimeSlotClick(clickTime);
    }
  };

  // Handle event drop
  const handleEventDrop =

  (event, dropHour) => {
    if (onEventDrop) {
      const newDateTime = moment(currentDate).hour(Math.floor(dropHour)).minute((dropHour % 1) * 60);
      onEventDrop(event, newDateTime);
    }
  };

  // Render day header
  const renderDayHeader =

  () => {
    const isTodayDate = isToday(currentDate);
    
    return (
      <div className="day-view-header">
        <div className={`day-header ${isTodayDate ? 'today' : ''}`}>
          <div className="day-name">{currentDate.format('dddd')}</div>
          <div className="day-date">{currentDate.format('MMMM D, YYYY')}</div>
        </div>
      </div>
    );
  };

  // Render all-day events section
  const renderAllDayEvents =

  () => {
    if (allDayEvents.length === 0) return null;

    const hasOverflow = allDayEvents.length > MAX_COLLAPSED_ALL_DAY_EVENTS;
    const visibleEvents = isAllDayExpanded
      ? allDayEvents
      : allDayEvents.slice(0, MAX_COLLAPSED_ALL_DAY_EVENTS);
    const hiddenCount = Math.max(0, allDayEvents.length - MAX_COLLAPSED_ALL_DAY_EVENTS);
    const allDayEventsId = `day-all-day-events-${currentDate.format('YYYY-MM-DD')}`;
    const toggleLabel = isAllDayExpanded
      ? 'Collapse all-day events'
      : `Show ${hiddenCount} more all-day event${hiddenCount === 1 ? '' : 's'}`;

    return (
      <div className={`all-day-section day-all-day-section ${isAllDayExpanded ? 'expanded' : 'collapsed'}`}>
        <div className="all-day-header">
          <div>
            <span className="all-day-label">All Day</span>
            <span className="all-day-count">
              {allDayEvents.length} event{allDayEvents.length === 1 ? '' : 's'}
            </span>
          </div>
          {hasOverflow && (
            <button
              type="button"
              className="all-day-toggle"
              aria-expanded={isAllDayExpanded}
              aria-controls={allDayEventsId}
              onClick={() => setIsAllDayExpanded((expanded) => !expanded)}
            >
              <AppIcon icon={isAllDayExpanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} aria-hidden="true" />
              <span>{toggleLabel}</span>
            </button>
          )}
        </div>
        <div id={allDayEventsId} className="all-day-events">
          {visibleEvents.map((event, index) => (
            <EventComponent
              key={`${event.id}-allday`}
              event={event}
              viewType={CALENDAR_VIEWS.DAY}
              isCompact={true}
              style={{
                position: 'relative',
                marginBottom: '4px',
                width: '100%',
                height: '30px'
              }}
              onEventClick={onEventClick}
              onEventEdit={onEventEdit}
              onEventDelete={onEventDelete}
              onConfirmResponsibility={onConfirmResponsibility}
              assignees={assignees}
              currentUser={currentUser}
            />
          ))}
          {hasOverflow && !isAllDayExpanded && (
            <button
              type="button"
              className="more-events-indicator all-day-more-button"
              onClick={() => setIsAllDayExpanded(true)}
            >
              +{hiddenCount} more all-day event{hiddenCount === 1 ? '' : 's'}
            </button>
          )}
        </div>
      </div>
    );
  };

  // Render time grid with current time indicator
  const renderTimeGrid =

  () => {
    const currentHour = moment().hour() + (moment().minute() / 60);
    const isTodayDate = isToday(currentDate);
    
    return (
      <div className="day-time-grid">
        {/* Current time indicator */}
        {isTodayDate && (
          <div 
            className="current-time-indicator"
            style={{ 
              top: `${currentHour * 60}px`,
              left: '60px',
              right: '10px'
            }}
          >
            <div className="time-line"></div>
            <div className="time-dot"></div>
          </div>
        )}
        
        {hourSlots.map((hourSlot, hourIndex) => {
          const hour = hourIndex;
          const isCurrentHour = isTodayDate && 
            hour <= currentHour && 
            currentHour < hour + 1;
          
          return (
            <div key={hourIndex} className={`time-slot-row ${isCurrentHour ? 'current-hour' : ''}`}>
              <div className="time-gutter">
                <span className="time-label">{formatTime(hourSlot)}</span>
              </div>
              
              <div
                className="time-slot"
                onClick={(e) => handleTimeSlotClick(hour, e)}
                onDrop={(e) => {
                  e.preventDefault();
                  const eventData = JSON.parse(e.dataTransfer.getData('application/json'));
                  handleEventDrop(eventData, hour);
                }}
                onDragOver={(e) => e.preventDefault()}
                data-hour={hour}
              >
                {/* Quarter-hour lines */}
                <div className="quarter-hour-line" style={{ top: '25%' }}></div>
                <div className="half-hour-line" style={{ top: '50%' }}></div>
                <div className="quarter-hour-line" style={{ top: '75%' }}></div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render timed events
  const renderTimedEvents =

  () => {
    return (
      <div className="day-events-overlay">
        {timedEvents.map((event) => (
                     <EventComponent
             key={event.id}
             event={event}
             viewType={CALENDAR_VIEWS.DAY}
             isDraggable={true}
             style={calculateEventStyle(event)}
             onEventClick={onEventClick}
             onEventEdit={onEventEdit}
             onEventDelete={onEventDelete}
             onConfirmResponsibility={onConfirmResponsibility}
             assignees={assignees}
             currentUser={currentUser}
           />
        ))}
      </div>
    );
  };

  // Render event summary
  const renderEventSummary =

  () => {
    if (dayEvents.length === 0) {
      return (
        <div className="day-summary">
          <p className="no-events">No events scheduled for this day</p>
        </div>
      );
    }

    const eventCounts = {
      total: dayEvents.length,
      confirmed: dayEvents.filter(e => e.confirmed).length,
      unconfirmed: dayEvents.filter(e => !e.confirmed).length,
      deadlines: dayEvents.filter(e => e.type && e.type.includes('deadline')).length
    };

    return (
      <div className="day-summary">
        <div className="event-counts">
          <span className="count-item">
            <strong>{eventCounts.total}</strong> events
          </span>
          {eventCounts.unconfirmed > 0 && (
            <span className="count-item unconfirmed">
              <strong>{eventCounts.unconfirmed}</strong> unconfirmed
            </span>
          )}
          {eventCounts.deadlines > 0 && (
            <span className="count-item deadlines">
              <strong>{eventCounts.deadlines}</strong> deadlines
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="day-view">
      {renderDayHeader()}
      {renderEventSummary()}
      {renderAllDayEvents()}
      <div className="day-content">
        {renderTimeGrid()}
        {renderTimedEvents()}
      </div>
    </div>
  );
};

export default DayView; 
